/**
 * Pull walking segments out of a `Route`. Mirrors iOS
 * `_extractWalkSegments`
 * (`metro-ios/MetroApp/MetroApp/MetroMap/MetroMapMetalRootView+RouteCard.swift`).
 *
 * Skip rules (in order):
 *   1. Non-walk legs.
 *   2. Walks whose `transition_id` RESOLVES in the schema — the
 *      transition layer already renders its pill (canonical case:
 *      Lambeth N. → Elephant & Castle = `transition-bak-EAC-tha-EPH1`).
 *      A walk carrying a transition_id the schema does NOT know (e.g. a
 *      street walk between same-name cluster siblings, Edgware Road
 *      Circle → Bakerloo) is DRAWN — skipping it would leave the route
 *      visually broken with nothing rendered between the two stations
 *      (2026-07 street-walk contract; previously ANY non-empty
 *      transition_id was skipped).
 *   3. Walks whose endpoints collide / are empty.
 *   4. Walks whose endpoints aren't in the schema snapshot (mid-render
 *      drift; the overlay must degrade silently rather than crash).
 *
 * An end that lands on a stop the route's highlight already covers with a
 * drawn transfer pill keeps its dots but loses its ring (`fromRing` /
 * `toRing` false): the ring would sit over that pill (Edgware Road street
 * walk onto the Circle ERC-0 ↔ ERC-1 interchange, owner 2026-10-20).
 *
 * Two entry points so the host can amortise the schema → coord-map cost
 * across rapid route-variant switches:
 *
 *   - `buildStationCoordIndex(schema)` builds a `Map<stationId, {x, y}>`
 *     once per schema load. Cache via `useMemo([schema])`.
 *   - `extractWalkSegmentsWithIndex(route, index)` is the per-route hot
 *     path — no Map construction, just O(walkLegs) lookups.
 *
 * The single-arg `extractWalkSegments(route, schema)` form is preserved
 * for callers that don't need the perf win (tests, one-shot diagnostic
 * uses) — it builds the index inline and discards it.
 */
import type { Route } from '@metro/routing'
import type { SchemaData } from '../schema/types'

export interface WalkSegment {
  readonly fromX: number
  readonly fromY: number
  readonly toX: number
  readonly toY: number
  /** Draw the endpoint ring at the walk's start. */
  readonly fromRing: boolean
  /** Draw the endpoint ring at the walk's end. */
  readonly toRing: boolean
}

export type StationCoordIndex = ReadonlyMap<string, { readonly x: number; readonly y: number }>

/**
 * Build the per-schema station → coord lookup. Stable for the lifetime
 * of a schema snapshot — callers should memoise on `[schema]`.
 */
export function buildStationCoordIndex(schema: SchemaData): StationCoordIndex {
  const out = new Map<string, { x: number; y: number }>()
  for (const s of schema.stations) out.set(s.id, { x: s.x, y: s.y })
  return out
}

/**
 * The set of transition ids the schema can actually render as a pill.
 * Stable per schema snapshot — memoise on `[schema]` alongside
 * {@link buildStationCoordIndex}.
 */
export function buildTransitionIdSet(schema: SchemaData): ReadonlySet<string> {
  const out = new Set<string>()
  for (const t of schema.transitions ?? []) out.add(t.id)
  return out
}

/**
 * Stops covered by a pill the route highlight draws: both ends of every
 * highlighted transfer except a street one, which the map never draws
 * (iOS / Android apply the same rule). Memoise on `[highlightedIds, schema]`.
 */
export function buildDrawnTransferStopIds(
  highlightedIds: ReadonlySet<string>,
  schema: SchemaData,
): ReadonlySet<string> {
  const out = new Set<string>()
  for (const t of schema.transitions ?? []) {
    if (!highlightedIds.has(t.id)) continue
    if (t.street === true) continue
    out.add(t.fromStationId)
    out.add(t.toStationId)
  }
  return out
}

export function extractWalkSegmentsWithIndex(
  route: Route,
  stationsById: StationCoordIndex,
  knownTransitionIds: ReadonlySet<string>,
  drawnTransferStopIds: ReadonlySet<string> = new Set(),
): WalkSegment[] {
  const out: WalkSegment[] = []
  for (const leg of route.legs) {
    if (leg.type !== 'walk') continue
    if (
      leg.transition_id !== undefined &&
      leg.transition_id.length > 0 &&
      knownTransitionIds.has(leg.transition_id)
    ) {
      continue
    }
    const fromID = leg.from_station_id ?? ''
    const toID = leg.to_station_id ?? ''
    if (fromID.length === 0 || toID.length === 0) continue
    if (fromID === toID) continue
    const from = stationsById.get(fromID)
    const to = stationsById.get(toID)
    if (from === undefined || to === undefined) continue
    out.push({
      fromX: from.x,
      fromY: from.y,
      toX: to.x,
      toY: to.y,
      fromRing: !drawnTransferStopIds.has(fromID),
      toRing: !drawnTransferStopIds.has(toID),
    })
  }
  return out
}

export function extractWalkSegments(route: Route, schema: SchemaData): WalkSegment[] {
  return extractWalkSegmentsWithIndex(
    route,
    buildStationCoordIndex(schema),
    buildTransitionIdSet(schema),
  )
}
